import { GOO_SOURCE_TILES, GOO_ZONE_ID, PERMANENT_WALL_TILES, ZONES } from '../domain/content.js';
import type { GameState } from '../contract.js';

const PERMANENT_WALL_SET = new Set(PERMANENT_WALL_TILES);
const GOO_SOURCE_SET = new Set(GOO_SOURCE_TILES);
const GOO_ZONE = ZONES.find((zone) => zone.id === GOO_ZONE_ID);

export function tileKey(x: number, y: number): string {
  return `${x},${y}`;
}

export function tileCenter(tile: number): number {
  return tile * 32 + 16;
}

export function pixelToTile(value: number): number {
  return Math.floor(value / 32);
}

export function generatedTileAmount(seed: number, x: number, y: number): number {
  if (isPermanentWall(x, y)) return 0;
  if (x === 0 && y === 0) return 0;
  if (x === 0 && y === 1) return 0;

  if (GOO_SOURCE_SET.has(tileKey(x, y))) return 5;

  const h = hashTile(seed, x, y);
  const densityRoll = h % 100;
  if (isInsideGooZone(x, y)) {
    if (densityRoll < 45) return 0;
    if (densityRoll < 66) return 1;
    if (densityRoll < 81) return 2;
    if (densityRoll < 92) return 3;
    return 4;
  }
  if (densityRoll < 7) return 0;
  if (densityRoll < 22) return 1;
  if (densityRoll < 42) return 2;
  if (densityRoll < 63) return 3;
  if (densityRoll < 81) return 4;
  if (densityRoll < 94) return 5;
  return 6;
}

export function generatedTileVariant(seed: number, x: number, y: number): number {
  return hashTile(seed ^ 0x51f15e0d, x, y) % 4;
}

export function getTileAmount(state: GameState, x: number, y: number): number {
  if (isPermanentWall(x, y)) return 0;
  const key = tileKey(x, y);
  const override = state.world.tileOverrides[key];
  return override ?? generatedTileAmount(state.seed, x, y);
}

export function setTileAmount(state: GameState, x: number, y: number, amount: number): void {
  if (isPermanentWall(x, y)) return;
  const key = tileKey(x, y);
  const next = Math.max(0, Math.min(6, Math.floor(amount)));
  state.world.tileOverrides[key] = next;
}

export function isRevealed(state: GameState, x: number, y: number): boolean {
  return state.world.revealedTiles[tileKey(x, y)] === true;
}

export function revealTile(state: GameState, x: number, y: number): void {
  state.world.revealedTiles[tileKey(x, y)] = true;
}

export function isPermanentWall(x: number, y: number): boolean {
  return PERMANENT_WALL_SET.has(tileKey(x, y));
}

function hashTile(seed: number, x: number, y: number): number {
  let h = seed | 0;
  h ^= Math.imul(x | 0, 0x45d9f3b);
  h ^= Math.imul(y | 0, 0x119de1f3);
  h ^= h >>> 16;
  h = Math.imul(h, 0x7feb352d);
  h ^= h >>> 15;
  h = Math.imul(h, 0x846ca68b);
  h ^= h >>> 16;
  return h >>> 0;
}

function isInsideGooZone(x: number, y: number): boolean {
  return GOO_ZONE !== undefined && x >= GOO_ZONE.minX && x < GOO_ZONE.minX + GOO_ZONE.width && y >= GOO_ZONE.minY && y < GOO_ZONE.minY + GOO_ZONE.height;
}
